// `/app/b/:siteId/foundation/website/domain/setup` (WP3b, D4 — CONSOLIDATION
// A7). The domain & mailbox wizard: UI ORCHESTRATION ONLY over the existing
// the canonical `/api/b/:businessId/domains*` + `.../mailboxes*` endpoints that components/domain-
// panel.tsx and components/mailbox-panel.tsx already call — no new backend
// endpoints (A7 hard rule). Reuses @pageboss/ui's `WizardStep` chrome (same
// composition pattern as routes/setup.tsx) across four steps:
//   1. choose  — bring-your-own-domain vs the free CF domain. Entitlement
//      rules are UNCHANGED: `canFreeDomain` comes straight from
//      GET /api/domains (server-derived from PLANS[tier].freeDomain + the
//      one-per-org index, same as domain-panel.tsx); a `tier_not_allowed`
//      error on either action renders the same UpgradeGate domain-panel.tsx
//      would.
//   2. dns     — the EXACT dns records domain-panel's own POST /api/domains
//      (connect) and POST /api/domains/:id/verify (recheck) responses
//      return, with copy-to-clipboard buttons. Only meaningful for a
//      bring-your-own domain (`registrar: 'external'`) — a free CF domain
//      needs no owner DNS action, so that path skips straight to step 3.
//   3. verify  — honest pending UI (status badge off the same DomainRow
//      shape) + a "recheck status" button that calls the SAME
//      POST /api/domains/:id/verify domain-panel.tsx's "Check status"
//      button calls (mirrors its exact mechanism: no client-side interval
//      polling anywhere in this repo for this endpoint, purely
//      button-triggered).
//   4. mailbox — mounts MailboxPanel VERBATIM (the smallest honest reuse
//      per the task brief) rather than re-implementing mailbox provisioning
//      or its billing-quantity rules (tierAllowsMailbox / billableMailboxes
//      stay entirely inside mailbox-panel.tsx / the API it calls).
//
// STATELESS BY DESIGN: this wizard writes nothing to onboarding_json and
// persists no "current step" anywhere. A returning visit re-derives the
// right step from the SAME live domain status domain-panel.tsx reads (GET
// /api/domains) — no domain → step 1; status !== 'active' (pending
// verification, or expired) → step 3 (mirrors "you're mid-flow, go wait/
// recheck"; the DNS-records step is a same-session transitional screen
// shown right after a fresh BYOD connect, not a place a returning visit
// re-derives into, since a raw GET /api/domains never carries dns records —
// domain-panel.tsx has the same gap, requiring an explicit "Check status"
// click to (re)load them); status === 'active' → step 4. `?step=` in the
// URL always wins (same precedent as routes/setup.tsx), so a step can be
// opened directly (e.g. the DNS step, to re-show records via an explicit
// recheck) without fighting the derivation.
//
// Every step's bottom "continue" button is real navigation, never a masked
// write — Continue on the `choose` step only advances once a domain has
// actually been connected/claimed in this session (nudges rather than
// silently jumping ahead); Continue on `dns`/`verify` always advances,
// because the destination step (verify / mailbox) honestly re-derives and
// re-displays whatever the live domain status actually is — there's no way
// to "skip past" a real gate this way, only skip past *this wizard's own
// copy* about it.
import { useEffect, useState } from 'react';
import { businessApi } from '@/lib/api-paths';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useBusinessId, useTenantParams } from '@/hooks/use-tenant-params';
import { Check, Copy } from 'lucide-react';
import { WizardStep } from '@pageboss/ui';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { UpgradeGate } from '@/components/upgrade-gate';
import { MailboxPanel } from '@/components/mailbox-panel';
import { businessRoutes, locationRoutes } from '@/lib/route-map';

// ---------------------------------------------------------------------
// Shapes — verbatim copies of domain-panel.tsx's own interfaces (A7: "the
// exact records the existing domain-panel APIs return"). Duplicated rather
// than imported because domain-panel.tsx doesn't export them; both files
// read the same `/api/b/:businessId/domains*` responses, so they must be kept in sync by
// hand if that API ever changes shape.
// ---------------------------------------------------------------------

interface DnsRecord {
  type: string;
  name: string;
  value: string;
}

interface DomainRow {
  id: string;
  domainName: string;
  status: 'pending_verification' | 'active' | 'expired' | 'deleted';
  registrar: 'cloudflare' | 'external';
  isFreeTier: boolean;
}

interface DomainsResponse {
  configured: boolean;
  domains: DomainRow[];
  canFreeDomain?: boolean;
}

interface DomainCandidate {
  name: string;
  pricing?: { registration_cost?: string };
}

function connectErrorCopy(code: string): string {
  switch (code) {
    case 'invalid_domain':
      return "That doesn't look like a valid domain. Enter something like yourbusiness.com.";
    case 'domain_taken':
      return 'That domain is already connected to another site.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

function registerErrorCopy(code: string): string {
  switch (code) {
    case 'free_domain_exists':
      return "You've already claimed your free domain.";
    case 'not_registrable':
      return "That domain isn't available. Try another name.";
    case 'registration_failed':
      return "Registration couldn't complete. Try a different domain, or connect one you own.";
    default:
      return 'Something went wrong. Please try again.';
  }
}

const STEP_KEYS = ['choose', 'dns', 'verify', 'mailbox'] as const;
type DomainWizardStep = (typeof STEP_KEYS)[number];

const TITLES: Record<DomainWizardStep, string> = {
  choose: 'connect your domain',
  dns: 'add these DNS records',
  verify: 'waiting on DNS verification',
  mailbox: 'set up professional email',
};

function isWizardStep(value: string | null): value is DomainWizardStep {
  return !!value && (STEP_KEYS as readonly string[]).includes(value);
}

// Derives the step for a returning visit from live domain status alone (see
// file header). Free-vs-BYOD doesn't change this: neither carries dns
// records on a plain GET, so both land on `verify`, which itself re-fetches
// records via the recheck endpoint for a BYOD domain.
function deriveStep(domain: DomainRow | null): DomainWizardStep {
  if (!domain) return 'choose';
  if (domain.status === 'active') return 'mailbox';
  return 'verify';
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // older browsers — the value is still visible inline
    }
  }

  return (
    <div className="grid grid-cols-[6rem_1fr_auto] items-center gap-x-4 gap-y-1 rounded-md bg-card p-3 text-sm border border-border-subtle">
      <span className="text-text-tertiary">{label}</span>
      <code className="break-all font-mono">{value}</code>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={`Copy ${label.toLowerCase()}`}
        className="shrink-0 rounded-md p-1.5 text-text-tertiary hover:bg-surface-sunken hover:text-foreground"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function DnsRecordCard({ record }: { record: DnsRecord }) {
  return (
    <div className="space-y-2 rounded-lg border border-border-subtle bg-surface-sunken p-3">
      <div className="flex items-center gap-2">
        <span className="eyebrow">Type</span>
        <code className="font-mono text-sm">{record.type}</code>
      </div>
      <CopyField label="Name" value={record.name} />
      <CopyField label="Value" value={record.value} />
    </div>
  );
}

function StatusBadge({ status }: { status: DomainRow['status'] }) {
  const variantMap: Record<DomainRow['status'], React.ComponentProps<typeof Badge>['variant']> = {
    active: 'success',
    pending_verification: 'warning',
    expired: 'muted',
    deleted: 'muted',
  };
  const labelMap: Record<DomainRow['status'], string> = {
    active: 'Active',
    pending_verification: 'Pending verification',
    expired: 'Expired',
    deleted: 'Removed',
  };
  return <Badge variant={variantMap[status]}>{labelMap[status]}</Badge>;
}

export function DomainWizard() {
  const { businessId = '', locationId: siteId } = useTenantParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [domain, setDomain] = useState<DomainRow | null>(null);
  const [dns, setDns] = useState<DnsRecord[]>([]);
  const [derivedStep, setDerivedStep] = useState<DomainWizardStep>('choose');

  // choose-step form state — mirrors domain-panel.tsx's own fields verbatim.
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [gate, setGate] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<DomainCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [freeAvailable, setFreeAvailable] = useState(false);
  const [claiming, setClaiming] = useState<string | null>(null);
  const [nudge, setNudge] = useState<string | null>(null);

  // verify-step state
  const [checking, setChecking] = useState(false);
  const [checkNotice, setCheckNotice] = useState<string | null>(null);

  const stepParam = searchParams.get('step');
  const active: DomainWizardStep = isWizardStep(stepParam) ? stepParam : derivedStep;
  const stepIndex = STEP_KEYS.indexOf(active) + 1;

  async function load(): Promise<DomainRow | null> {
    if (!siteId) return null;
    const res = await fetch(businessApi(businessId, `domains?locationId=${encodeURIComponent(siteId)}`));
    if (res.status === 401) {
      window.location.href = '/login';
      return null;
    }
    const data = (await res.json()) as DomainsResponse;
    setConfigured(data.configured !== false);
    setFreeAvailable(data.canFreeDomain === true);
    const found = data.domains.find((d) => d.status !== 'deleted') ?? null;
    setDomain(found);
    return found;
  }

  useEffect(() => {
    setLoading(true);
    void load().then((found) => {
      setDerivedStep(deriveStep(found));
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  function goTo(step: DomainWizardStep) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('step', step);
      return next;
    });
  }

  async function connect() {
    setBusy(true);
    setConnectError(null);
    setGate(null);
    setNudge(null);
    try {
      const res = await fetch(businessApi(businessId, 'domains'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ siteId, domainName: input }),
      });
      const data = (await res.json()) as {
        disabled?: boolean;
        error?: string;
        dns?: DnsRecord[];
        id?: string;
      };
      if (data.disabled) {
        setConfigured(false);
        return;
      }
      if (!res.ok) {
        if (data.error === 'tier_not_allowed') {
          setGate('Custom domains need a paid plan.');
        } else {
          setConnectError(connectErrorCopy(data.error ?? ''));
        }
        return;
      }
      setDns(data.dns ?? []);
      setInput('');
      await load();
      goTo(data.dns && data.dns.length > 0 ? 'dns' : 'verify');
    } catch {
      setConnectError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function search() {
    setSearching(true);
    setConnectError(null);
    try {
      const res = await fetch(businessApi(businessId, `domains/search?q=${encodeURIComponent(q)}`));
      if (res.status === 401) {
        window.location.href = '/login';
        return;
      }
      const data = (await res.json()) as { disabled?: boolean; domains?: DomainCandidate[] };
      if (data.disabled || res.status === 403) {
        setFreeAvailable(false);
        return;
      }
      if (res.ok) setResults(data.domains ?? []);
    } catch {
      // leave results unchanged
    } finally {
      setSearching(false);
    }
  }

  async function claim(name: string) {
    const ok = window.confirm(
      `Register ${name} for free? This reserves the domain for your account and can't be undone.`,
    );
    if (!ok) return;
    setClaiming(name);
    setConnectError(null);
    setGate(null);
    setNudge(null);
    try {
      const res = await fetch(businessApi(businessId, 'domains/register'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ siteId, domainName: name }),
      });
      const data = (await res.json()) as { disabled?: boolean; error?: string };
      if (data.disabled) {
        setFreeAvailable(false);
        return;
      }
      if (!res.ok) {
        if (data.error === 'tier_not_allowed') {
          setGate('Free domains are included on every paid plan.');
        } else {
          setConnectError(registerErrorCopy(data.error ?? ''));
        }
        return;
      }
      setResults([]);
      await load();
      // Free (CF-registered) domains carry no owner DNS step — CF wires the
      // hostname automatically — so this always goes straight to `verify`.
      goTo('verify');
    } catch {
      setConnectError('Something went wrong. Please try again.');
    } finally {
      setClaiming(null);
    }
  }

  // Same mechanism as domain-panel.tsx's "Check status" button — the ONE
  // recheck call, POST /api/domains/:id/verify, always returns fresh dns +
  // status (A7: "status polling" here means button-triggered recheck, not a
  // client interval — domain-panel.tsx has no interval either).
  async function recheck() {
    if (!domain) return;
    setChecking(true);
    setCheckNotice(null);
    try {
      const res = await fetch(businessApi(businessId, `domains/${domain.id}/verify`), { method: 'POST' });
      const data = (await res.json().catch(() => ({}))) as {
        dns?: DnsRecord[];
        status?: string;
        disabled?: boolean;
      };
      if (data.dns) setDns(data.dns);
      if (data.status !== 'active') {
        setCheckNotice("Still pending — DNS changes can take a little while to propagate. Try again soon.");
      }
      await load();
    } finally {
      setChecking(false);
    }
  }

  if (!siteId) return null;

  if (loading) {
    return <div className="mx-auto h-40 max-w-xl animate-pulse rounded-lg bg-muted" aria-hidden="true" />;
  }

  if (!configured) {
    return (
      <div className="mx-auto max-w-xl rounded-lg border border-border-subtle bg-surface-sunken p-6 text-sm text-text-secondary">
        Custom domains aren&rsquo;t running on your account yet &mdash; that&rsquo;s on our side,
        not anything you did. Your site stays live on its pageboss address meanwhile, and there is
        nothing here to set up until we switch it on; email support@pageboss.com if you need one
        sooner.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <WizardStep
        step={active}
        index={stepIndex}
        total={STEP_KEYS.length}
        title={TITLES[active]}
        continueLabel={active === 'mailbox' ? 'done' : 'continue'}
        onContinue={() => {
          if (active === 'choose') {
            if (domain) {
              goTo(dns.length > 0 ? 'dns' : 'verify');
            } else {
              setNudge('Connect or claim a domain above to continue.');
            }
            return;
          }
          if (active === 'dns') {
            goTo('verify');
            return;
          }
          if (active === 'verify') {
            goTo('mailbox');
            return;
          }
          // 'mailbox' — this is the last step; Continue just lands on the
          // standalone Mailboxes screen (the same one the nav's unlocked
          // Mailboxes item points at once a domain is active).
          navigate(businessRoutes.mailboxes(businessId));
        }}
      >
        {active === 'choose' ? (
          <ChooseStep
            freeAvailable={freeAvailable}
            q={q}
            setQ={setQ}
            results={results}
            searching={searching}
            claiming={claiming}
            onSearch={() => void search()}
            onClaim={(name) => void claim(name)}
            input={input}
            setInput={setInput}
            busy={busy}
            onConnect={() => void connect()}
            connectError={connectError}
            gate={gate}
            nudge={nudge}
            siteId={siteId}
          />
        ) : null}

        {active === 'dns' ? <DnsStep records={dns} domain={domain} /> : null}

        {active === 'verify' ? (
          <VerifyStep
            domain={domain}
            dns={dns}
            checking={checking}
            checkNotice={checkNotice}
            onRecheck={() => void recheck()}
            siteId={siteId}
          />
        ) : null}

        {active === 'mailbox' ? <MailboxPanel siteId={siteId} /> : null}
      </WizardStep>
    </div>
  );
}

function ChooseStep({
  freeAvailable,
  q,
  setQ,
  results,
  searching,
  claiming,
  onSearch,
  onClaim,
  input,
  setInput,
  busy,
  onConnect,
  connectError,
  gate,
  nudge,
  siteId,
}: {
  freeAvailable: boolean;
  q: string;
  setQ: (v: string) => void;
  results: DomainCandidate[];
  searching: boolean;
  claiming: string | null;
  onSearch: () => void;
  onClaim: (name: string) => void;
  input: string;
  setInput: (v: string) => void;
  busy: boolean;
  onConnect: () => void;
  connectError: string | null;
  gate: string | null;
  nudge: string | null;
  siteId: string;
}) {
  // Canonical tenant URLs carry the Business id; this is only rendered inside
  // one, so it comes from the router rather than being threaded as a prop.
  const businessId = useBusinessId() ?? '';
  return (
    <div className="space-y-4">
      {freeAvailable ? (
        <div className="space-y-4 rounded-lg border border-border-subtle bg-card p-6">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Get a free domain</h3>
            <p className="mt-1 text-sm text-text-secondary">
              Included with every paid plan. Search for a name and claim it — we set up everything
              for you.
            </p>
          </div>
          <div className="flex gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="your business name"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && q.trim().length >= 2 && !searching) onSearch();
              }}
            />
            <Button type="button" disabled={searching || q.trim().length < 2} onClick={onSearch}>
              Search
            </Button>
          </div>
          {results.length > 0 ? (
            <ul className="space-y-2">
              {results.map((r) => (
                <li
                  key={r.name}
                  className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-surface-sunken px-3 py-2 text-sm"
                >
                  <span className="flex items-center gap-2">
                    <code className="font-mono text-foreground">{r.name}</code>
                    <Badge variant="success">Free</Badge>
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={claiming !== null}
                    onClick={() => onClaim(r.name)}
                  >
                    {claiming === r.name ? 'Claiming…' : 'Claim free'}
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-4 rounded-lg border border-border-subtle bg-card p-6">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Connect your domain</h3>
          <p className="mt-1 text-sm text-text-secondary">
            Connect a domain you already own — like{' '}
            <code className="font-mono text-xs">yourbusiness.com</code>. You'll get DNS records to
            add at your registrar.
          </p>
        </div>
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="yourbusiness.com"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && input.trim().length > 0 && !busy) onConnect();
            }}
          />
          <Button type="button" disabled={busy || input.trim().length === 0} onClick={onConnect}>
            Connect
          </Button>
        </div>
        {gate ? (
          <UpgradeGate capability={gate} planKey="pro" from={window.location.pathname} />
        ) : connectError ? (
          <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">{connectError}</p>
        ) : null}
      </div>

      {nudge ? <p className="text-sm text-text-tertiary">{nudge}</p> : null}

      <p className="text-xs text-text-tertiary">
        Already have a domain connected?{' '}
        <Link to={locationRoutes.websiteDomain(businessId, siteId)} className="underline underline-offset-2 hover:text-text-secondary">
          Manage it directly →
        </Link>
      </p>
    </div>
  );
}

function DnsStep({ records, domain }: { records: DnsRecord[]; domain: DomainRow | null }) {
  if (!domain) {
    return (
      <p className="text-sm text-text-secondary">
        No domain connected yet — go back and connect or claim one first.
      </p>
    );
  }
  if (domain.registrar === 'cloudflare') {
    return (
      <p className="text-sm text-text-secondary">
        No DNS setup needed — we manage this domain's records for you automatically.
      </p>
    );
  }
  if (records.length === 0) {
    return (
      <p className="text-sm text-text-secondary">
        No records to show yet. Continue to the verification step to load them.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-text-secondary">
        Add these records at <code className="font-mono text-xs">{domain.domainName}</code>'s DNS
        provider, then continue.
      </p>
      <p className="text-xs text-text-tertiary">
        Connecting a root domain (like <code className="font-mono">acme.com</code>, without{' '}
        <code className="font-mono">www</code>)? Some DNS providers can't point a root domain with
        these records. If yours rejects them, use{' '}
        <code className="font-mono">www.{domain.domainName}</code> instead, or move your domain's
        DNS to Cloudflare (free) — that handles it automatically.
      </p>
      <div className="space-y-2">
        {records.map((r) => (
          <DnsRecordCard key={`${r.type}-${r.name}`} record={r} />
        ))}
      </div>
    </div>
  );
}

function VerifyStep({
  domain,
  dns,
  checking,
  checkNotice,
  onRecheck,
  siteId,
}: {
  domain: DomainRow | null;
  dns: DnsRecord[];
  checking: boolean;
  checkNotice: string | null;
  onRecheck: () => void;
  siteId: string;
}) {
  // Canonical tenant URLs carry the Business id; this is only rendered inside
  // one, so it comes from the router rather than being threaded as a prop.
  const businessId = useBusinessId() ?? '';
  if (!domain) {
    return (
      <p className="text-sm text-text-secondary">
        No domain connected yet — go back and connect or claim one first.
      </p>
    );
  }

  if (domain.status === 'expired') {
    return (
      <div className="space-y-3">
        <p className="text-sm text-text-secondary">
          <code className="font-mono text-xs">{domain.domainName}</code> expired before
          verification completed.
        </p>
        <Link
          to={locationRoutes.websiteDomain(businessId, siteId)}
          className="inline-flex w-fit items-center text-sm font-medium text-brand-700 hover:text-brand-800 hover:underline"
        >
          Remove it and try connecting again →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <code className="font-mono text-sm">{domain.domainName}</code>
        <StatusBadge status={domain.status} />
      </div>

      {domain.status === 'pending_verification' ? (
        <div className="space-y-3 rounded-lg border border-warning-bg bg-warning-bg/40 p-4">
          <p className="text-sm text-text-secondary">
            {domain.registrar === 'cloudflare'
              ? "We're setting this domain up for you. This usually only takes a few minutes."
              : "We haven't seen your DNS records yet. Changes can take a little while to propagate."}
          </p>
          {dns.length > 0 ? (
            <div className="space-y-2">
              {dns.map((r) => (
                <DnsRecordCard key={`${r.type}-${r.name}`} record={r} />
              ))}
            </div>
          ) : null}
          <Button type="button" variant="outline" size="sm" disabled={checking} onClick={onRecheck}>
            {checking ? 'Checking…' : 'Recheck status'}
          </Button>
          {checkNotice ? <p className="text-xs text-text-tertiary">{checkNotice}</p> : null}
        </div>
      ) : null}

      {domain.status === 'active' ? (
        <p className="text-sm text-text-secondary">
          Verified — <code className="font-mono text-xs">{domain.domainName}</code> is live. Continue
          to set up professional email.
        </p>
      ) : null}
    </div>
  );
}
